<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcPropertyUnits',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_showModal_0ruvwwbj1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'PropertyUnitEditor',
      props: {
        mode: 'add',
        parent: props.parent,
      },
    };
  };
  const act_2585509888553580_emit_7lobqm1a1_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2585509888553580_close_1c3j7dldp_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2585509888553580_showModal_902gl7edy_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'PropertyUnitEditor',
      props: {
        parent: props.parent,
        item: $event.scope.item,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_5v9o8xhgi =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_e80fw90ft =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5v9o8xhgi,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_e80fw90ft],
    event: 'reload',
  });
  const act_2095089031469157_showModal_0ruvwwbj1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_0ruvwwbj1_getActionArgs,
    });
  const inst_evh_2095089031469157_clickPreventStop_utfwtkjqr =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_0ruvwwbj1,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_utfwtkjqr],
      event: 'click.prevent.stop',
    });
  const act_2585509888553580_emit_7lobqm1a1 = new actionTypes.EmitAction({
    actionArgs: act_2585509888553580_emit_7lobqm1a1_getActionArgs,
  });
  const inst_evh_2585509888553580_close_1c3j7dldp =
    new actionTypes.EventHandlerImpl({
      action: act_2585509888553580_emit_7lobqm1a1,
      event: 'close',
      when: inst_evh_2585509888553580_close_1c3j7dldp_getWhen,
      displayName: 'emit',
    });
  const act_2585509888553580_showModal_902gl7edy =
    new actionTypes.ShowModalAction({
      actionArgs: act_2585509888553580_showModal_902gl7edy_getActionArgs,
      events: [inst_evh_2585509888553580_close_1c3j7dldp],
    });
  const inst_evh_2585509888553580_clickPreventStop_7tfg5l21q =
    new actionTypes.EventHandlerImpl({
      action: act_2585509888553580_showModal_902gl7edy,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2585509888553580_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2585509888553580_clickPreventStop_7tfg5l21q],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_e80fw90ft,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_clickPreventStop_utfwtkjqr,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_2585509888553580_close_1c3j7dldp,
    inst_evh_2585509888553580_clickPreventStop_7tfg5l21q,
    inst_evh_2585509888553580_clickPreventStop,
  };

  const evh_7315092382398562_reload_e80fw90ft = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_e80fw90ft.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_utfwtkjqr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_utfwtkjqr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2585509888553580_close_1c3j7dldp = async (event, scope) => {
    return await actions.inst_evh_2585509888553580_close_1c3j7dldp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2585509888553580_clickPreventStop_7tfg5l21q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2585509888553580_clickPreventStop_7tfg5l21q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2585509888553580_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2585509888553580_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Property', 'Units') }}
          </strong>
          <ItpButton
            :text="gettext('Add unit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!parent.units || !parent.units.length">
          <ItpText>
            {{ pgettext('Property', 'No units have been added yet.') }}
          </ItpText>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          v-for="item in _.orderBy(parent.units, ['sort_order', 'name'])"
        >
          <ItpBox alignItems="center">
            <ItpText :text="item.name" class="fw-bold"> </ItpText>
            <ItpIcon
              :title="gettext('Locked')"
              name="fa-lock"
              class="px-3"
              v-if="item.locked"
            >
            </ItpIcon>
            <ItpButton
              icon="fa-pen"
              :tooltip="gettext('Edit')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!parent.readonly"
              @click.prevent.stop="
                evh_2585509888553580_clickPreventStop($event, { item })
              "
            >
            </ItpButton>
          </ItpBox>
          <ItpBox>
            <ItpText :text="item.description" pre> </ItpText>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
